/**
 * Register an EPC Contract directly (no tender / estimation / award upstream) —
 * e.g. contracts signed outside the system or already running at go-live.
 */
import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { EpcClientSelect } from '@/components/epc/EpcClientSelect';
import {
  apiErrorMessage,
  isDuplicateContractError,
} from '@/components/epc/commercial-ux/epcContractErrors';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { ProjectSelector } from '@/components/project/ProjectSelector';
import { epcCommercialService } from '@/services/epcCommercial.service';
import {
  CommercialModelExplainer,
  CommercialModelSelect,
} from '@/components/epc/commercial-ux/EpcCommercialModelGuide';
import { commercialModelInfo, recommendedContentTab } from '@/constants/epcCommercialModels';

const { Title, Text } = Typography;
const { TextArea } = Input;

const VAT_TREATMENTS = [
  { value: 'STANDARD', label: 'Standard rated' },
  { value: 'ZERO_RATED', label: 'Zero rated' },
  { value: 'EXEMPT', label: 'Exempt' },
  { value: 'OUT_OF_SCOPE', label: 'Out of scope' },
];

const fmtDate = (v: unknown) => (v ? dayjs(v as Dayjs).format('YYYY-MM-DD') : null);

const EpcContractCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const [customerId, setCustomerId] = useState<string | undefined>();
  const selectedModel = Form.useWatch('commercialModel', form) as string | undefined;
  const isCostPlus = commercialModelInfo(selectedModel)?.key === 'COST_PLUS';

  const createM = useMutation({
    mutationFn: (payload: Record<string, unknown>) => epcCommercialService.createDirectContract(payload),
    onSuccess: (row) => {
      message.success(`Contract ${String(row.contract_number || '')} registered as Draft`);
      qc.invalidateQueries({ queryKey: ['epc-contracts'] });
      navigate(
        `/epc/commercial/contracts/${String(row.id)}?caTab=${recommendedContentTab(row.commercial_model)}`
      );
    },
  });

  const submit = (values: Record<string, unknown>, allowDuplicate = false) => {
    const payload = {
      customerId: values.customerId,
      title: values.title,
      clientContractRef: values.clientContractRef || null,
      commercialModel: values.commercialModel,
      contractValue: values.contractValue ?? null,
      currency: values.currency || 'AED',
      departmentId: values.departmentId,
      signedDate: fmtDate(values.signedDate),
      commencementDate: fmtDate(values.commencementDate),
      completionDate: fmtDate(values.completionDate),
      projectId: values.projectId || null,
      particulars: {
        payment_terms: values.paymentTerms || null,
        retention_pct: values.retentionPct ?? null,
        advance_payment_pct: values.advancePaymentPct ?? null,
        performance_bond_pct: values.performanceBondPct ?? null,
        ld_daily_rate: values.ldDailyRate ?? null,
        defects_liability_months: values.defectsLiabilityMonths ?? null,
        vat_treatment: values.vatTreatment || null,
        fee_pct: values.feePct ?? null,
        scope_summary: values.scopeSummary || null,
      },
      allowDuplicate,
    };
    createM.mutate(payload, {
      onError: (e) => {
        if (isDuplicateContractError(e)) {
          Modal.confirm({
            title: 'Possible duplicate contract',
            content: apiErrorMessage(e, 'A contract with this client reference already exists.'),
            okText: 'Register anyway',
            onOk: () => submit(values, true),
          });
          return;
        }
        message.error(apiErrorMessage(e, 'Could not register contract'));
      },
    });
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/epc/commercial/contracts')}>
          Contracts
        </Button>
      </Space>
      <Title level={3} style={{ marginBottom: 4 }}>
        Register contract
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        For contracts signed without going through tender, estimation and award in the system. The
        contract starts as Draft: add the Schedule of Values or payment milestones, then submit it
        for approval and activate.
      </Text>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Already running?"
        description="If work has already been certified under this contract, record the opening position (certified to date, retention held, advance recovered) on the contract before the first IPC."
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{ currency: 'AED', commercialModel: 'Lump Sum', vatTreatment: 'STANDARD' }}
        onFinish={(v) => submit(v)}
        disabled={createM.isPending}
      >
        <Card size="small" title="Contract" style={{ marginBottom: 12 }}>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="customerId"
                label="Client"
                rules={[{ required: true, message: 'Client is required to invoice certified IPCs' }]}
              >
                <EpcClientSelect
                  onClientChange={(id) => {
                    setCustomerId(id);
                    form.setFieldValue('projectId', undefined);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="clientContractRef" label="Client contract reference">
                <Input placeholder="Reference on the signed agreement / LOA" maxLength={120} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="title" label="Contract title" rules={[{ required: true, message: 'Required' }]}>
                <Input placeholder="Project / package title" maxLength={500} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="commercialModel" label="Commercial model" rules={[{ required: true }]}>
                <CommercialModelSelect />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="contractValue"
                label="Contract value (excl. VAT)"
                rules={[{ required: true, message: 'Required' }]}
                extra="Can be set from the Schedule of Values total later"
              >
                <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="currency" label="Currency">
                <Select
                  options={['AED', 'USD', 'EUR', 'SAR', 'GBP'].map((v) => ({ value: v, label: v }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="departmentId"
                label="Department"
                rules={[{ required: true, message: 'Department is required' }]}
              >
                <DepartmentSelect />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="projectId"
                label="Existing project (optional)"
                extra="Link a project that is already running; otherwise mobilization creates one"
              >
                <ProjectSelector
                  allowClear
                  customerId={customerId}
                  placeholder="Select existing project"
                  onChange={(pid) => form.setFieldValue('projectId', pid)}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <CommercialModelExplainer model={selectedModel} />
            </Col>
          </Row>
        </Card>

        <Card size="small" title="Dates" style={{ marginBottom: 12 }}>
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item name="signedDate" label="Signed / LOA date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="commencementDate" label="Commencement">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="completionDate"
                label="Completion"
                dependencies={['commencementDate']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const start = getFieldValue('commencementDate');
                      if (!value || !start || !dayjs(value).isBefore(dayjs(start), 'day')) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Completion cannot be before commencement'));
                    },
                  }),
                ]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card size="small" title="Payment terms" style={{ marginBottom: 12 }}>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="paymentTerms" label="Payment terms">
                <Input placeholder="e.g. 30 days from certified IPC" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="vatTreatment" label="VAT treatment">
                <Select options={VAT_TREATMENTS} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="defectsLiabilityMonths" label="Defects liability (months)">
                <InputNumber style={{ width: '100%' }} min={0} max={120} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="retentionPct" label="Retention %">
                <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="advancePaymentPct" label="Advance %">
                <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="performanceBondPct" label="Performance bond %">
                <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="ldDailyRate" label="LD per day">
                <InputNumber style={{ width: '100%' }} min={0} step={1} />
              </Form.Item>
            </Col>
            {isCostPlus ? (
              <Col xs={12} md={6}>
                <Form.Item
                  name="feePct"
                  label="Fee % on reimbursable cost"
                  rules={[{ required: true, message: 'Cost Plus needs the agreed fee %' }]}
                >
                  <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} />
                </Form.Item>
              </Col>
            ) : null}
            <Col xs={24}>
              <Form.Item name="scopeSummary" label="Scope summary">
                <TextArea rows={3} placeholder="Works / packages covered by this contract" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Space>
          <Button type="primary" htmlType="submit" loading={createM.isPending}>
            Register contract
          </Button>
          <Button onClick={() => navigate('/epc/commercial/contracts')}>Cancel</Button>
        </Space>
      </Form>
    </div>
  );
};

export default EpcContractCreatePage;
